/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import Table from 'components/Table';
import {
  deleteGiroComercial, getGirosComerciales, patchGiroComercial, postGiroComercial,
} from 'api/empresas/girosComerciales';
import { getTiposDeGirosComerciales } from 'api/empresas/tiposDeGiroComercial';
import { onError } from 'utils/handlers';
import { useSelector } from 'react-redux';
import { basePermissioPropTypes } from 'utils/permission/propTypes';
import { INTEGER_PATTERN_REPLACE } from 'utils/normalizers';
import FormSubmitControls from './FormSubmitControls';
import NumericInput from './NumericInput';
import Select from './Select';
import ModalDelete from './ModalDelete';

const { useBreakpoint } = Grid;

export const permissionConfig = {
  title: 'Giros de Comercio',
  model: 'girocomercial',
  catalogs: [
    'tipodegirocomercial',
  ],
};
const GirosDeComercios = ({
  empresa,
  modulo,
  permission,
  justView,
}) => {
  const {
    estadosGlobales,
  } = useSelector((state) => state.catalogs);
  const screen = useBreakpoint();
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState([]);
  const [tiposDeGiroComercial, setTiposDeGiroComercial] = useState([]);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const tiene_horario_extraordinario = Form.useWatch('tiene_horario_extraordinario', form);

  const fetchData = async () => {
    setLoading(true);
    if (empresa) {
      const _data = await getGirosComerciales({ empresa });
      setData(_data);
    } else if (modulo) {
      const _data = await getGirosComerciales({ modulo });
      setData(_data);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const params = empresa ? { empresa } : { modulo };
      const [_data, _tiposDeGiroComercial] = await Promise.all([
        await getGirosComerciales(params),
        getTiposDeGirosComerciales(),
      ]);
      if (mounted) {
        setData(_data);
        setTiposDeGiroComercial(_tiposDeGiroComercial);
        setLoading(false);
      }
    };
    if (empresa || modulo) fetchAll();
    return () => { mounted = false; };
  }, [empresa]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };
  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record?.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      if (empresa) {
        values.empresa = empresa;
      } else {
        values.expediente_modulo = modulo;
      }
      const [key] = selectedRowKeys;
      const saved = await (key ? patchGiroComercial(key, values) : postGiroComercial(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteGiroComercial(key);
    if (deleted) {
      setVisibleAlert(false);
      await fetchData();
    }
  };

  const getWidthModal = () => {
    if (screen.md) {
      return '80%';
    }
    return '100%';
  };

  const columns = [
    {
      titleText: 'Clave',
      key: 'id',
      dataIndex: 'id',
      width: 150,
    },
    {
      titleText: 'Tipo de Giro Comercial',
      key: 'tipo_de_giro_comercial',
      dataIndex: 'tipo_de_giro_comercial',
      render: (val) => tiposDeGiroComercial.find((item) => item?.id === val)?.descripcion || 'N/A',
      width: 300,
    },
    {
      titleText: 'Cantidad de Unidades',
      key: 'cantidad_de_unidades',
      dataIndex: 'cantidad_de_unidades',
      width: 300,
    },
  ];

  const commonProps = {
    cols: columns,
    data: data || [],
    rowSelection,
    handleOnRowClick: justView ? null : handleOnRowClick,
    rowKey: 'id',
    allowExpand: false,
    pagination: false,
    allowSort: false,
  };

  return (
    <Spin
      tip="Cargando..."
      spinning={loading}
    >
      <Table
        {...commonProps}
        {...(justView ? {
          hasAllViewPermission: true,
          allowAdd: false,
          allowEdit: false,
          allowDelete: false,
        } : { permission, controls: { onClickAdd, onClickEdit, onClickDelete } })}
      />

      <Modal
        open={visible}
        closable={false}
        footer={null}
        centered
        width={getWidthModal()}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Giro de Comercio`}
            onFinish={onFinish}
            onCancel={onCancel}
            loading={loading}
          />
        )}
        keyboard={!loading}
        maskClosable={!loading}
        forceRender
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={() => onFinish()}
          initialValues={{
            estados_globales: 1,
          }}
        >
          <Row gutter={10}>
            {!!selectedRowKeys.length && (
            <Col span={12}>
              <Form.Item
                name="id"
                label="Clave"
                hasFeedback
              >
                <Input disabled />
              </Form.Item>
            </Col>
            )}
            <Col span={12}>
              <Form.Item
                name="cuenta_objeto"
                label="Objeto cuenta"
                hasFeedback
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="tipo_de_giro_comercial"
                label="Tipo de giro"
              >
                <Select dataSource={tiposDeGiroComercial} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <NumericInput
                name="cantidad_de_metros2"
                label="Cantidad de metros"
                decimal
                allowZero
              />
            </Col>
            <Col span={12}>
              <Form.Item
                name="cantidad_de_unidades"
                label="Cantidad de unidades"
                normalize={(val) => val.replace(INTEGER_PATTERN_REPLACE, '')}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cantidad_de_empleados"
                label="Cantidad de empleados"
                normalize={(val) => val.replace(INTEGER_PATTERN_REPLACE, '')}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="¿Tiene horario extraordinario?"
                name="tiene_horario_extraordinario"
              >
                <Select trueFalse />
              </Form.Item>
            </Col>
            {tiene_horario_extraordinario && (
            <Col span={12}>
              <NumericInput
                name="cantidad_de_horas_extraordinarias"
                label="Cantidad de horas extraordinarias"
                rules={[{ required: true }]}
              />
            </Col>
            )}
            <Col span={24}>
              <Form.Item
                name="observaciones"
                label="Observaciones"
                hasFeedback
              >
                <Input.TextArea
                  autoSize={{ minRows: 4, maxRows: 4 }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="estados_globales"
                label="Estado"
                hasFeedback
              >
                <Select
                  disabled={!selectedRowKeys.length}
                  dataSource={estadosGlobales}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Row>
        </Form>
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="Giro seleccionado"
        loading={loading}
      />
    </Spin>

  );
};

GirosDeComercios.propTypes = {
  empresa: PropTypes.number,
  modulo: PropTypes.number,
  permission: basePermissioPropTypes,
  justView: PropTypes.bool,
};

GirosDeComercios.defaultProps = {
  empresa: null,
  modulo: null,
  permission: {},
  justView: false,
};

export default GirosDeComercios;
